import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaUtensils,
  FaStar,
  FaCrown,
  FaLeaf,
  FaUsers,
  FaCalendarAlt,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaCheckCircle,
  FaPlusCircle,
  FaTrash,
  FaClipboardList,
  FaConciergeBell,
  FaSearch,
  FaInfoCircle,
  FaTimes,
  FaTag,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { ApiError } from '../../api/client';
import { createBulkEnquiry } from '../../api/mealPlansApi';
import { Link } from 'react-router-dom';
import {
  apiGetProfile,
  DIGIRASA_AUTH_UPDATED_EVENT,
  profilePayloadToForm,
  readStoredAuth,
} from '../Account/profileFormUtils';
import {
  contactFromProfilePayload,
  contactFromStoredUser,
  mergeCheckoutContact,
} from '../Checkout/checkoutContactUtils';
import { sanitizePhoneDigitsInput, validatePhoneDigitsValue } from '../../utils/phone';
import {
  genRefId,
  getEventDateValidationMessage,
  getMinEventDate,
  isoToDdMmYyyy,
  isVegCustomDish,
  normalizeEventDateToIso,
} from './bulkOrderUtils';
import useBulkOrderCarousel from './useBulkOrderCarousel';
import southThaliImg from '../../assets/images/south-india-thali.png';
import southThaliMenuImg from '../../assets/images/south-thali-menu.png';
import biryaniRaitaImg from '../../assets/images/biryani-raita.jpg';
import zeeraRiceDalImg from '../../assets/images/zeera-rice-dal.jpg';
import digirasaFusionMenuImg from '../../assets/images/digirasa-fusion-menu.png';
import masalaDosaImg from '../../assets/images/masala-dosa.jpg';
import gulabJamunImg from '../../assets/images/gulab-jamun.jpg';
import chapatiImg from '../../assets/images/chapati-product.jpg';
import methiParathaImg from '../../assets/images/methi-paratha.jpg';
import mirchiBajjiImg from '../../assets/images/mirchi-bajji.png';
import sweetLassiImg from '../../assets/images/sweet-lassi.png';
import soupImg from '../../assets/images/soup.png';
import rotiImg from '../../assets/images/roti.png';
import pickleImg from '../../assets/images/pickle.png';
import friedRiceRaitaImg from '../../assets/images/fried-rice-raita.png';
import spinachImg from '../../assets/images/spinach.png';
import thaliImg from '../../assets/images/thali.png';
import juiceImg from '../../assets/images/juice.png';
import ingredientsImg from '../../assets/images/ingredients.jpg';
import './BulkOrder.css';

// ─── Standard Menu Packages ───────────────────────────────────────────────────
const PACKAGES = [
  {
    id: 'traditional-south-basic',
    icon: <FaLeaf />,
    tag: 'BASIC',
    name: 'Traditional South Basic Menu',
    subtitle: 'Wholesome South Indian comfort spread',
    minGuests: 20,
    color: 'green',
    image: southThaliImg,
    description:
      'A heartwarming spread of traditional South Indian staples — rice, dal, curry, fry, chutney, sambar, rasam, pulihora and more. Pure vegetarian, deeply satisfying, and universally loved.',
    tags: ['Basic', 'South Indian', 'Traditional'],
    pricing: [
      { plates: 20, price: 375 },
      { plates: 30, price: 375 },
      { plates: 50, price: 375 },
      { plates: 75, price: 325 },
      { plates: 100, price: 325 },
    ],
    items: [
      'White Rice',
      'Dal',
      'Curry',
      'Fry',
      'Chutney',
      'Sambar',
      'Rasam',
      'Pulihora',
      'Bajji / Masala Vada',
      'Sweet',
      'Ghee',
      'Curd',
      'Papad & Fryums',
    ],
  },
  {
    id: 'traditional-south-premium',
    icon: <FaStar />,
    tag: 'PREMIUM',
    name: 'Traditional South Premium Menu',
    subtitle: 'Elevated South Indian feast with extra variety',
    minGuests: 20,
    color: 'orange',
    featured: true,
    image: southThaliMenuImg,
    description:
      'An elevated South Indian spread with a wider variety — seasonal dal, 2 veg curries, 2 chutneys, kandi podi and more. The perfect premium choice for special occasions.',
    tags: ['Vegetarian', 'South Indian', 'Premium'],
    pricing: [
      { plates: 20, price: 500 },
      { plates: 30, price: 500 },
      { plates: 50, price: 500 },
      { plates: 75, price: 450 },
      { plates: 100, price: 450 },
    ],
    items: [
      'White Rice',
      'Seasonal Dal',
      '2 Veg Curries',
      'Fry',
      "2 Chutney's",
      'Kandi Podi',
      'Sambar',
      'Rasam',
      'Pulihora',
      'Bajji / Masala Vada',
      'Sweet',
      'Ghee',
      'Curd',
      'Papad & Fryums',
    ],
  },
  {
    id: 'traditional-north-basic',
    icon: <FaUtensils />,
    tag: 'BASIC',
    name: 'Traditional North Basic Menu',
    subtitle: 'Classic North Indian vegetarian spread',
    minGuests: 20,
    color: 'green',
    image: thaliImg,
    description:
      'A traditional North Indian menu with roti, rice, dal, paneer curry, veg starter, raita, sweet, and classic accompaniments.',
    tags: ['Basic', 'North Indian', 'Traditional'],
    pricing: [
      { plates: 20, price: 450 },
      { plates: 30, price: 450 },
      { plates: 50, price: 450 },
      { plates: 75, price: 400 },
      { plates: 100, price: 400 },
    ],
    items: [
      'Phulka / Roti',
      'Veg / Gobi Manchurian',
      'Jeera Rice / Veg Pulao',
      'Dal Tadka',
      'Paneer Curry',
      'Aloo Jeera / Aloo Methi',
      'Raita',
      'Sweet',
      'Papad N Fryums',
    ],
  },
  {
    id: 'traditional-north-premium',
    icon: <FaCrown />,
    tag: 'PREMIUM',
    name: 'Traditional North Premium Menu',
    subtitle: 'Expanded premium North Indian celebration menu',
    minGuests: 20,
    color: 'gold',
    image: thaliImg,
    description:
      'A premium North Indian spread with multiple choices across breads, starters, biryani/rice, dals, curries, salad, sweets, and accompaniments.',
    tags: ['Premium', 'North Indian', 'Traditional', 'Vegetarian'],
    pricing: [
      { plates: 20, price: 650 },
      { plates: 30, price: 650 },
      { plates: 50, price: 650 },
      { plates: 75, price: 600 },
      { plates: 100, price: 600 },
    ],
    items: [
      'Phulka / Roti / Poori',
      'Veg / Gobi Manchurian',
      'Chilli Veg / Chilli Paneer',
      'Dum Biryani / Veg Pulao / Paneer Biryani',
      'Dal Tadka / Dal Makhni / Dal Chatpata / Navratan Dal',
      'Chole / Rajma Masala / Aloo Gobi Masala',
      'Paneer Butter Masala / Kadai Paneer / Palak Paneer',
      'Mix Veg Curry (Dry)',
      'Salad',
      'Raita',
      'Gulab Jamun & One Optional Sweet',
      'Papad N Fryums',
    ],
  },
  {
    id: 'digirasa-fusion',
    icon: <FaCrown />,
    tag: 'FUSION',
    name: 'Digirasa Fusion Menu',
    subtitle: 'A grand fusion spread for celebrations',
    minGuests: 20,
    color: 'gold',
    image: digirasaFusionMenuImg,
    description:
      'A grand celebration menu combining South and North Indian cuisine — welcome drink, pulao, phulka/poori, paneer curry, brinjal masala, 2 sweets, raita and much more.',
    tags: ['Vegetarian', 'Fusion', 'Celebration', 'Premium'],
    pricing: [
      { plates: 20, price: 650 },
      { plates: 30, price: 650 },
      { plates: 50, price: 650 },
      { plates: 75, price: 600 },
      { plates: 100, price: 600 },
    ],
    items: [
      'Seasonal Welcome Drink',
      'White Rice',
      'Veg Pulao',
      'Phulka / Poori',
      'Brinjal Masala Curry',
      'Paneer Curry',
      'Veg Curry',
      'Fry',
      'Seasonal Dal',
      'Sambar',
      'Rasam',
      "2 Chutney's",
      'Kandi Podi',
      'Bajji / Masala Vada',
      '2 Sweets',
      'Ghee',
      'Curd',
      'Raita',
      'Papad & Fryums',
    ],
  },
];

// ─── Custom Menu Item Bank ─────────────────────────────────────────────────────
const MENU_BANK = {
  Starters: [
    'Veg Spring Rolls',
    'Paneer Tikka',
    'Samosa (2 pcs)',
    'Pani Puri Counter',
    'Dahi Puri',
    'Onion Bajji',
    'Mirchi Bajji',
    'Veg Cutlet',
    'Hara Bhara Kebab',
    'Paneer 65',
    'Crispy Corn',
    'Baby Corn 65',
    'Gobi 65',
  ],
  'Rice & Biryani': [
    'Veg Biryani',
    'Jeera Rice',
    'Ghee Rice',
    'Lemon Rice',
    'Curd Rice',
    'Tomato Rice',
    'Coconut Rice',
    'Veg Fried Rice',
    'Paneer Pulao',
    'Mushroom Biryani',
  ],
  Curries: [
    'Paneer Butter Masala',
    'Shahi Paneer',
    'Dal Makhani',
    'Methi Malai Paneer',
    'Mix Veg Curry',
    'Palak Paneer',
    'Chana Masala',
    'Rajma',
    'Kadai Paneer',
    'Veg Korma',
    'Malai Kofta',
    'Aloo Gobi',
    'Veg Jalfrezi',
    'Mushroom Masala',
  ],
  Breads: [
    'Butter Naan',
    'Plain Roti',
    'Paratha',
    'Garlic Naan',
    'Puri',
    'Bajra Roti',
    'Stuffed Paratha',
  ],
  'South Indian': [
    'Plain Idly (2)',
    'Masala Dosa',
    'Rava Upma',
    'Sambar',
    'Coconut Chutney',
    'Filter Coffee',
    'Vada (2)',
    'Bisibelebath',
    'Rasam',
    'Pesarattu',
    'Pongal',
    'Curd Vada',
  ],
  Desserts: [
    'Gulab Jamun',
    'Rasgulla',
    'Kheer',
    'Payasam',
    'Kesari',
    'Gajar Halwa',
    'Phirni',
    'Ice Cream (Vanilla)',
    'Bobbatlu',
    'Fruit Custard',
  ],
  Beverages: [
    'Buttermilk (Chaas)',
    'Masala Chai',
    'Lemonade',
    'Mango Juice',
    'Sweet Lassi',
    'Filter Coffee',
    'Coconut Water',
    'Cold Drink (Assorted)',
    'Jaljeera',
    'Badam Milk',
  ],
  Extras: ['Raita', 'Salad', 'Papad', 'Pickle', 'Mouth Freshener', 'Soup'],
};

// ─── Item Info Bank (image + description + tags for each dish) ───────────────
const ITEM_INFO = {
  // Classic South Indian package items
  'Idly (2 pcs) + Vada (1 pc)': {
    image: southThaliMenuImg,
    description:
      'Soft steamed rice-and-lentil cakes paired with a crispy urad-dal fritter. Served with hot sambar and fresh coconut chutney. A South Indian breakfast staple loved by all ages.',
    tags: ['Vegetarian', 'Steamed', 'South Indian'],
  },
  'Sambar & Coconut Chutney': {
    image: southThaliImg,
    description:
      'Tangy lentil-and-vegetable stew slow-cooked with tamarind and spices, accompanied by freshly ground coconut chutney tempered with mustard and curry leaves.',
    tags: ['Vegetarian', 'Gluten Free', 'South Indian'],
  },
  'Steamed Rice + Dal Tadka': {
    image: zeeraRiceDalImg,
    description:
      'Fluffy long-grain steamed rice paired with a vibrant yellow dal tempered with mustard, cumin, ghee and dry red chillies. A wholesome everyday comfort.',
    tags: ['Vegetarian', 'Comfort Food', 'Protein Rich'],
  },
  'Bisibelebath or Vangi Bath': {
    image: southThaliMenuImg,
    description:
      'Bisibelebath is a hearty one-pot rice-and-lentil dish simmered in spiced tamarind broth. Vangi bath is fragrant brinjal rice. Both served with a dollop of ghee.',
    tags: ['Vegetarian', 'South Indian', 'One-pot'],
  },
  'Dry Sabzi (seasonal)': {
    image: ingredientsImg,
    description:
      'A freshly prepared seasonal dry vegetable side — beans, carrot, aloo or mixed veg — stir-fried with mustard, curry leaves and mild spices.',
    tags: ['Vegetarian', 'Seasonal', 'Healthy'],
  },
  'Gravy Curry': {
    image: thaliImg,
    description:
      'A rich slow-cooked gravy curry with fresh tomatoes, onions and house spice blend. Pairs beautifully with rice or roti.',
    tags: ['Vegetarian', 'Main Course'],
  },
  'Papad + Pickle': {
    image: pickleImg,
    description:
      'Crisp roasted papad with house-made mango or mixed pickle — the perfect crunchy, tangy accompaniment to any thali.',
    tags: ['Vegetarian', 'Condiment', 'Crunchy'],
  },
  'Sweet – Kesari / Payasam': {
    image: gulabJamunImg,
    description:
      'Kesari is a saffron-laced semolina halwa with cashews and raisins. Payasam is a creamy milk-based pudding scented with cardamom — both celebrate the South Indian tradition of ending a meal sweetly.',
    tags: ['Sweet', 'Vegetarian', 'Traditional'],
  },
  'Buttermilk (seasonal)': {
    image: sweetLassiImg,
    description:
      'Chilled and lightly spiced buttermilk — the ideal digestif to cool down and round off a South Indian meal.',
    tags: ['Beverage', 'Cooling', 'Probiotic'],
  },
  // Andhra items
  'Andhra Boiled Rice': {
    image: friedRiceRaitaImg,
    description:
      'Soft-cooked raw rice in the traditional Andhra style — the perfect base for pappu, pulusu, gongura chutney and all the bold Andhra sides.',
    tags: ['Vegetarian', 'Andhra', 'Gluten Free'],
  },
  'Pappu (Lentil Curry)': {
    image: zeeraRiceDalImg,
    description:
      'Andhra-style lentil curry tempered with mustard, curry leaves and green chillies. Comforting, protein-rich and a staple of every Andhra household.',
    tags: ['Vegetarian', 'Andhra', 'Protein Rich'],
  },
  'Gongura Chutney': {
    image: ingredientsImg,
    description:
      'The iconic tangy sorrel leaf chutney — a uniquely Andhra condiment with a sour, spicy kick that elevates every bite. A signature you cannot skip.',
    tags: ['Vegetarian', 'Andhra', 'Signature', 'Tangy'],
  },
  'Pulusu (Tamarind Gravy)': {
    image: thaliImg,
    description:
      'A traditional Andhra thin gravy made with tamarind, onions and spices — tangy, light and deeply flavourful. Poured over rice for the quintessential Andhra experience.',
    tags: ['Vegetarian', 'Andhra', 'Tangy'],
  },
  'Dry Fry Curry (seasonal)': {
    image: ingredientsImg,
    description:
      'Seasonal vegetables or protein stir-fried Andhra-style with red chillies, mustard and fresh curry leaves. Crispy, bold and packed with flavour.',
    tags: ['Andhra', 'Spicy', 'Seasonal'],
  },
  Rasam: {
    image: soupImg,
    description:
      'A thin, peppery tomato-tamarind broth tempered with mustard and cumin. Perfect as a digestive soup or mixed with steamed rice to finish the meal.',
    tags: ['Vegetarian', 'South Indian', 'Digestive', 'Light'],
  },
  'Curd / Raita': {
    image: friedRiceRaitaImg,
    description:
      'Fresh homemade curd — cooling and creamy. Best enjoyed with rice to balance the heat of spicy Andhra dishes or as a standalone cool side.',
    tags: ['Vegetarian', 'Cooling', 'Probiotic'],
  },
  'Papad + Avakai Pickle': {
    image: pickleImg,
    description:
      'Crisp papad with authentic Avakai — the legendary Andhra raw mango pickle prepared with mustard powder and red chilli for bold, pungent, unforgettable flavour.',
    tags: ['Vegetarian', 'Andhra', 'Condiment', 'Spicy'],
  },
  'Sweet – Bobbatlu / Pootharekulu': {
    image: gulabJamunImg,
    description:
      'Bobbatlu are sweet lentil-filled flatbreads drizzled with ghee. Pootharekulu are delicate paper-thin rice-starch sweets filled with jaggery and coconut — a true culinary art form.',
    tags: ['Sweet', 'Andhra', 'Traditional', 'Festival'],
  },
  // North Indian items
  'Paneer Butter Masala': {
    image: thaliImg,
    description:
      "Velvety cubes of cottage cheese in a rich butter-tomato gravy with cream and aromatic spices. One of India's most iconic and universally adored curries.",
    tags: ['Vegetarian', 'Rich', 'Crowd Pleaser', 'North Indian'],
  },
  'Dal Makhani': {
    image: zeeraRiceDalImg,
    description:
      'Black lentils and kidney beans slow-cooked overnight in a buttery tomato base with cream — deep, indulgent and impossibly comforting. Best with naan.',
    tags: ['Vegetarian', 'North Indian', 'Rich', 'Slow Cooked'],
  },
  'Jeera Rice': {
    image: zeeraRiceDalImg,
    description:
      'Fragrant basmati rice toasted with whole cumin seeds in ghee — light, aromatic and the ideal partner for any North Indian gravy.',
    tags: ['Vegetarian', 'North Indian', 'Light', 'Aromatic'],
  },
  'Butter Naan / Roti (3 pcs)': {
    image: chapatiImg,
    description:
      'Pillowy soft naan baked in a tandoor and brushed generously with butter — or freshly made whole-wheat roti. Warm, soft and endlessly scoopable.',
    tags: ['Bread', 'Vegetarian', 'Tandoor'],
  },
  'Salad + Raita': {
    image: friedRiceRaitaImg,
    description:
      'Fresh garden salad of cucumber, tomato and onion with lemon dressing, alongside creamy boondi or cucumber raita. Light, fresh and essential.',
    tags: ['Vegetarian', 'Fresh', 'Cooling', 'Light'],
  },
  'Gulab Jamun (2 pcs)': {
    image: gulabJamunImg,
    description:
      'Soft milk-solid dumplings fried golden and soaked in rose-cardamom sugar syrup. A timeless dessert that ends every feast on a sweet, indulgent note.',
    tags: ['Sweet', 'Vegetarian', 'Classic', 'Popular'],
  },
  'Masala Buttermilk': {
    image: sweetLassiImg,
    description:
      'Tangy spiced buttermilk with roasted jeera, ginger and curry leaves — a satisfying digestive drink to close a North Indian meal.',
    tags: ['Beverage', 'Cooling', 'Digestive'],
  },
  // Premium package items
  'Welcome Drink (Jaljeera / Sharbat)': {
    image: juiceImg,
    description:
      'A refreshing spiced lemon-cumin welcome drink or a vibrant rose/fruit sharbat — the perfect first impression to greet your guests in style.',
    tags: ['Beverage', 'Welcome', 'Refreshing'],
  },
  'Soup (Tomato / Lemon Coriander)': {
    image: soupImg,
    description:
      'A warm, tangy tomato shorba or a light lemony coriander broth with subtle pepper — an elegant opening course for any grand event.',
    tags: ['Starter', 'Hot', 'Light'],
  },
  'Biryani (Veg / Chicken – choice)': {
    image: biryaniRaitaImg,
    description:
      'Long-grain basmati layered with marinated veg or chicken, slow-dum-cooked with whole spices, saffron and caramelised onions. Served with raita. The crown jewel of the spread.',
    tags: ['Main Course', 'Biryani', 'Signature', 'Crowd Pleaser'],
  },
  'Paneer Curry + Dal Tadka': {
    image: zeeraRiceDalImg,
    description:
      'A hearty combination of paneer in a rich gravy alongside vibrant yellow dal tadka — together covering both indulgence and comfort.',
    tags: ['Vegetarian', 'Main Course', 'Combination'],
  },
  '2 Seasonal Curries': {
    image: thaliImg,
    description:
      "Two carefully chosen seasonal curries — one dry, one gravy-based — prepared with the day's freshest produce from local markets.",
    tags: ['Vegetarian', 'Seasonal', 'Fresh'],
  },
  'Pulao / Flavoured Rice': {
    image: friedRiceRaitaImg,
    description:
      'Aromatic flavoured rice — vegetable pulao, coconut rice or lemon rice — light and fragrant, prepared with premium basmati.',
    tags: ['Vegetarian', 'Rice', 'Aromatic'],
  },
  'Butter Naan + Roti (4 pcs)': {
    image: chapatiImg,
    description:
      'A generous combination of tandoor-baked buttered naan and fresh whole-wheat roti — soft, warm and perfect for scooping rich gravies.',
    tags: ['Bread', 'Vegetarian', 'Combo'],
  },
  'Salad Bar + Raita': {
    image: friedRiceRaitaImg,
    description:
      'An interactive fresh salad spread with toppings and dressings, plus a choice of creamy raita — guests serve themselves for a personalised experience.',
    tags: ['Fresh', 'Vegetarian', 'Interactive'],
  },
  'Papad, Pickle & Mouth Freshener': {
    image: pickleImg,
    description:
      'Crisp assorted papads, a selection of house pickles and a refreshing saunf-mishri mouth freshener to beautifully close the meal.',
    tags: ['Condiment', 'Digestive', 'Post-meal'],
  },
  'Dessert Combo – Gulab Jamun + Kheer': {
    image: gulabJamunImg,
    description:
      'A duo of indulgent desserts — velvety gulab jamun in sugar syrup paired with creamy slow-cooked rice kheer scented with cardamom and saffron.',
    tags: ['Sweet', 'Combo', 'Premium', 'Crowd Pleaser'],
  },
  'Cold Beverage': {
    image: juiceImg,
    description:
      'Chilled lemonade, mango juice, rose sharbat or packaged cold drink — the perfect refreshing finish to a grand celebration spread.',
    tags: ['Beverage', 'Chilled', 'Refreshing'],
  },
  // Custom bank — Starters
  'Veg Spring Rolls': {
    image: ingredientsImg,
    description:
      'Crispy golden rolls filled with stir-fried vegetables, glass noodles and spices. Light, crunchy and a universal crowd-pleaser at any event.',
    tags: ['Starter', 'Vegetarian', 'Crispy'],
  },
  'Paneer Tikka': {
    image: ingredientsImg,
    description:
      'Chunks of paneer marinated in spiced yoghurt and chargrilled in a tandoor. Smoky, juicy and unmistakably delicious — always the first to disappear.',
    tags: ['Starter', 'Vegetarian', 'Tandoor', 'Smoky'],
  },
  'Samosa (2 pcs)': {
    image: ingredientsImg,
    description:
      'Flaky pastry triangles filled with spiced potato and peas, fried to a perfect golden crisp. Served with mint chutney and tamarind sauce.',
    tags: ['Starter', 'Vegetarian', 'Classic', 'Fried'],
  },
  'Pani Puri Counter': {
    image: ingredientsImg,
    description:
      'A live interactive counter where guests fill crisp puris with tangy tamarind water, potatoes and sprouts. Always the most popular station at any event!',
    tags: ['Live Counter', 'Vegetarian', 'Street Food', 'Interactive'],
  },
  'Dahi Puri': {
    image: ingredientsImg,
    description:
      'Crisp puris topped with potato, chickpeas, sweet chutney, tangy tamarind and a generous drizzle of chilled curd. A burst of textures and flavours.',
    tags: ['Starter', 'Vegetarian', 'Street Food'],
  },
  'Onion Bajji': {
    image: ingredientsImg,
    description:
      'Thin onion rings dipped in spiced chickpea batter and deep-fried until golden and crispy. A classic snack elevated for events.',
    tags: ['Starter', 'Vegetarian', 'Fried'],
  },
  'Mirchi Bajji': {
    image: mirchiBajjiImg,
    description:
      'Long green chillies stuffed with a tangy peanut-tamarind filling, coated in gram flour batter and fried to perfection. A beloved Hyderabadi street snack.',
    tags: ['Starter', 'Vegetarian', 'Hyderabadi', 'Spicy'],
  },
  'Veg Cutlet': {
    image: ingredientsImg,
    description:
      'Pan-fried patties of mixed vegetables, potatoes and spices coated in breadcrumbs — crispy outside, soft and flavourful inside.',
    tags: ['Starter', 'Vegetarian', 'Crispy'],
  },
  'Hara Bhara Kebab': {
    image: spinachImg,
    description:
      'Vibrant green patties made from spinach, peas, paneer and spices — nutritious, colourful and packed with fresh flavour.',
    tags: ['Starter', 'Vegetarian', 'Healthy', 'Iron Rich'],
  },
  // Custom bank — Rice & Biryani
  'Veg Biryani': {
    image: biryaniRaitaImg,
    description:
      'Fragrant basmati layered with spiced seasonal vegetables, caramelised onions and fresh mint — dum-cooked for deep aroma and flavour.',
    tags: ['Main Course', 'Vegetarian', 'Biryani', 'Aromatic'],
  },
  'Ghee Rice': {
    image: zeeraRiceDalImg,
    description:
      'Premium basmati cooked with clarified butter, whole spices and fried cashews — aromatic, indulgent and a perfect companion to any curry.',
    tags: ['Rice', 'Vegetarian', 'Rich', 'Aromatic'],
  },
  'Lemon Rice': {
    image: friedRiceRaitaImg,
    description:
      'Tangy South Indian lemon rice tempered with mustard, chana dal, turmeric and fresh curry leaves — bright, zesty and refreshing.',
    tags: ['Rice', 'Vegetarian', 'South Indian', 'Tangy'],
  },
  'Curd Rice': {
    image: friedRiceRaitaImg,
    description:
      'Comforting soft rice mixed with fresh curd and tempered with mustard, ginger and pomegranate. The perfect cooling end to a spicy meal.',
    tags: ['Rice', 'Vegetarian', 'Cooling', 'Comfort'],
  },
  'Tomato Rice': {
    image: friedRiceRaitaImg,
    description:
      'Rice cooked with fresh tomatoes, onions and South Indian spices — tangy, flavourful and incredibly satisfying.',
    tags: ['Rice', 'Vegetarian', 'South Indian', 'Tangy'],
  },
  'Coconut Rice': {
    image: southThaliImg,
    description:
      'Soft rice tossed with freshly grated coconut, green chillies, mustard and curry leaves — light, aromatic and a favourite South Indian variety.',
    tags: ['Rice', 'Vegetarian', 'South Indian', 'Light'],
  },
  // Custom bank — Curries
  'Shahi Paneer': {
    image: thaliImg,
    description:
      'Paneer in a royal Mughlai gravy enriched with nuts, cream and aromatic spices — luscious, rich and fit for a celebration.',
    tags: ['Curry', 'Vegetarian', 'Rich', 'Mughlai'],
  },
  'Methi Malai Paneer': {
    image: methiParathaImg,
    description:
      'Paneer cubes in a velvety fenugreek-cream sauce — mildly bitter from methi, rich from cream, utterly luxurious.',
    tags: ['Curry', 'Vegetarian', 'Rich', 'Creamy'],
  },
  'Mix Veg Curry': {
    image: thaliImg,
    description:
      'A medley of seasonal vegetables simmered in a fragrant onion-tomato gravy with whole spices and a hint of cream.',
    tags: ['Curry', 'Vegetarian', 'Seasonal', 'Healthy'],
  },
  'Palak Paneer': {
    image: spinachImg,
    description:
      'Tender paneer cubes in a velvety purée of slow-cooked spinach, garlic and mild spices — vibrant green, hearty and nutritious.',
    tags: ['Curry', 'Vegetarian', 'Iron Rich', 'Healthy'],
  },
  'Chana Masala': {
    image: ingredientsImg,
    description:
      'Bold chickpeas cooked in a tangy, spiced tomato-onion masala with amchur and coriander — robust, textured and protein-packed.',
    tags: ['Curry', 'Vegetarian', 'Protein Rich', 'Tangy'],
  },
  Rajma: {
    image: zeeraRiceDalImg,
    description:
      'Hearty red kidney beans slow-cooked in a thick spiced North Indian gravy. Comforting, protein-packed and perfect with jeera rice.',
    tags: ['Curry', 'Vegetarian', 'North Indian', 'Comfort'],
  },
  'Kadai Paneer': {
    image: thaliImg,
    description:
      'Paneer and capsicum tossed in a bold kadai masala of whole spices, coriander and fresh tomatoes — rustic, fragrant and flavourful.',
    tags: ['Curry', 'Vegetarian', 'Bold', 'Rustic'],
  },
  // Custom bank — Breads
  'Butter Naan': {
    image: chapatiImg,
    description:
      'Soft pillowy naan baked in a tandoor and finished with a generous brush of butter — the ultimate bread for scooping up rich curries.',
    tags: ['Bread', 'Vegetarian', 'Tandoor', 'Buttery'],
  },
  'Plain Roti': {
    image: rotiImg,
    description:
      'Freshly made whole-wheat flatbread, rolled thin and cooked on a tawa. Simple, wholesome and essential — pairs with any curry.',
    tags: ['Bread', 'Vegetarian', 'Whole Wheat', 'Healthy'],
  },
  Paratha: {
    image: methiParathaImg,
    description:
      'Flaky layered whole-wheat flatbread pan-fried with ghee until crispy and golden. Delicious plain or stuffed — great for breakfast or mains.',
    tags: ['Bread', 'Vegetarian', 'Flaky', 'Rich'],
  },
  'Garlic Naan': {
    image: chapatiImg,
    description:
      'Naan baked with a topping of minced garlic and coriander — the garlicky aroma alone makes it irresistible for the entire table.',
    tags: ['Bread', 'Vegetarian', 'Tandoor', 'Garlic', 'Popular'],
  },
  Puri: {
    image: rotiImg,
    description:
      'Small deep-fried whole-wheat rounds that puff up beautifully — light, crispy and perfect alongside curries, halwa or dal.',
    tags: ['Bread', 'Vegetarian', 'Fried', 'Light'],
  },
  'Bajra Roti': {
    image: rotiImg,
    description:
      'Pearl millet flatbread — a rustic, nutritious alternative to wheat roti with a distinctive earthy flavour. Especially good with ghee and jaggery.',
    tags: ['Bread', 'Vegetarian', 'Healthy', 'Millets', 'Nutritious'],
  },
  'Stuffed Paratha': {
    image: methiParathaImg,
    description:
      'Golden paratha stuffed with spiced potato, paneer or methi filling — crispy, hearty and a complete meal in itself.',
    tags: ['Bread', 'Vegetarian', 'Stuffed', 'Filling'],
  },
  // Custom bank — South Indian
  'Plain Idly (2)': {
    image: southThaliMenuImg,
    description:
      'Pillowy soft steamed rice-and-lentil cakes, light on the stomach and perfect with sambar and coconut chutney. The gentlest breakfast dish.',
    tags: ['South Indian', 'Vegetarian', 'Steamed', 'Light'],
  },
  'Masala Dosa': {
    image: masalaDosaImg,
    description:
      'A golden crispy rice crepe filled with spiced potato masala, served with sambar and an assortment of chutneys. A South Indian icon with centuries of history.',
    tags: ['South Indian', 'Vegetarian', 'Crispy', 'Classic'],
  },
  'Rava Upma': {
    image: southThaliImg,
    description:
      'Semolina cooked with onions, green chillies, curry leaves and seasonal vegetables — light, savoury and a beloved South Indian breakfast.',
    tags: ['South Indian', 'Vegetarian', 'Light', 'Breakfast'],
  },
  Sambar: {
    image: soupImg,
    description:
      'Lentil and mixed vegetable stew simmered with tamarind and a freshly ground spice blend — the heartbeat of South Indian cooking.',
    tags: ['South Indian', 'Vegetarian', 'Lentil', 'Tangy'],
  },
  'Coconut Chutney': {
    image: southThaliImg,
    description:
      'Freshly ground coconut blended with roasted chana dal and green chillies, tempered with mustard and curry leaves. Creamy, fresh and essential.',
    tags: ['South Indian', 'Vegetarian', 'Condiment', 'Fresh'],
  },
  'Filter Coffee': {
    image: ingredientsImg,
    description:
      'Strong South Indian coffee with chicory, brewed through a metal filter and blended with frothy hot milk. Served in the traditional tumbler-davara set. A cultural institution.',
    tags: ['Beverage', 'South Indian', 'Hot', 'Signature'],
  },
  'Vada (2)': {
    image: southThaliMenuImg,
    description:
      'Crispy donut-shaped fritters made from urad dal batter — crunchy outside, soft inside. Best dunked in sambar for a satisfying contrast.',
    tags: ['South Indian', 'Vegetarian', 'Crispy', 'Fried'],
  },
  Bisibelebath: {
    image: southThaliMenuImg,
    description:
      "Karnataka's iconic one-pot dish — rice, toor dal and vegetables cooked in a freshly ground spice powder with tamarind and ghee. Served piping hot with a dollop of butter.",
    tags: ['South Indian', 'Vegetarian', 'One-pot', 'Karnataka'],
  },
  // Custom bank — Desserts
  'Gulab Jamun': {
    image: gulabJamunImg,
    description:
      'Soft khoya dumplings soaked in rose and cardamom sugar syrup — melt-in-the-mouth, warm and irresistibly sweet. The dessert that makes every event memorable.',
    tags: ['Dessert', 'Vegetarian', 'Classic', 'Warm'],
  },
  Rasgulla: {
    image: gulabJamunImg,
    description:
      'Light, spongy cottage cheese balls soaked in a delicate sugar syrup. Soft, airy and refreshingly sweet — a Bengali gem loved across India.',
    tags: ['Dessert', 'Vegetarian', 'Light', 'Bengali'],
  },
  Kheer: {
    image: gulabJamunImg,
    description:
      'Creamy slow-cooked rice pudding enriched with full-fat milk, sugar, saffron and cardamom, garnished with cashews and pistachios. A classic at every celebration.',
    tags: ['Dessert', 'Vegetarian', 'Creamy', 'Slow Cooked'],
  },
  Payasam: {
    image: southThaliMenuImg,
    description:
      'South Indian kheer — made with vermicelli or moong dal, sweetened with jaggery or sugar and flavoured with cardamom. A traditional ritual-ending sweet.',
    tags: ['Dessert', 'Vegetarian', 'South Indian', 'Traditional'],
  },
  Kesari: {
    image: gulabJamunImg,
    description:
      'Saffron-yellow semolina halwa cooked with ghee, sugar and garnished with cashews and raisins. Rich, fragrant and deeply satisfying — a wedding staple.',
    tags: ['Dessert', 'Vegetarian', 'South Indian', 'Ghee Rich'],
  },
  'Gajar Halwa': {
    image: gulabJamunImg,
    description:
      "Slow-cooked grated carrots simmered in full-fat milk and ghee with sugar and cardamom. A winter classic that's hard to resist at any time of year.",
    tags: ['Dessert', 'Vegetarian', 'Seasonal', 'Slow Cooked'],
  },
  Phirni: {
    image: gulabJamunImg,
    description:
      'A North Indian ground rice pudding set in clay pots, chilled and garnished with pistachios, rose petals and saffron. Elegant and deeply fragrant.',
    tags: ['Dessert', 'Vegetarian', 'Chilled', 'Elegant'],
  },
  'Ice Cream (Vanilla)': {
    image: ingredientsImg,
    description:
      'Classic creamy vanilla ice cream — pure, cold and perfect as a palate cleanser or a crowd-pleaser for children and adults alike.',
    tags: ['Dessert', 'Cold', 'Popular', 'Universal'],
  },
  Bobbatlu: {
    image: gulabJamunImg,
    description:
      'Sweet lentil-stuffed flatbreads from Andhra — thin, crispy and drizzled with ghee. A festival-time delicacy made with care and tradition.',
    tags: ['Dessert', 'Vegetarian', 'Andhra', 'Traditional', 'Festival'],
  },
  // Custom bank — Beverages
  'Buttermilk (Chaas)': {
    image: sweetLassiImg,
    description:
      'Chilled lightly spiced buttermilk with jeera, ginger and mint — cooling, probiotic and the best digestive companion after a rich meal.',
    tags: ['Beverage', 'Cooling', 'Probiotic', 'Digestive'],
  },
  'Masala Chai': {
    image: ingredientsImg,
    description:
      'Strong, sweet tea brewed with ginger, cardamom, cinnamon and cloves. Warm, aromatic and a universal crowd-pleaser for morning and evening events.',
    tags: ['Beverage', 'Hot', 'Spiced', 'Signature'],
  },
  Lemonade: {
    image: juiceImg,
    description:
      'Freshly squeezed lemon juice with chilled water and a choice of sugar or rock salt, finished with mint. Bright, refreshing and endlessly thirst-quenching.',
    tags: ['Beverage', 'Refreshing', 'Cold', 'Fresh'],
  },
  'Mango Juice': {
    image: juiceImg,
    description:
      'Thick, luscious Alphonso or Totapuri mango pulp blended with chilled water and sugar — a tropical celebration in a glass, especially popular in summer.',
    tags: ['Beverage', 'Seasonal', 'Popular', 'Tropical'],
  },
  'Sweet Lassi': {
    image: sweetLassiImg,
    description:
      'Thick yoghurt blended with sugar, cardamom and rose water — creamy, indulgent and an ideal festive beverage for all occasions.',
    tags: ['Beverage', 'Vegetarian', 'Sweet', 'Creamy'],
  },
  'Coconut Water': {
    image: juiceImg,
    description:
      'Natural tender coconut water — isotonic, hydrating and the most refreshing drink you can offer on a warm day. Served chilled.',
    tags: ['Beverage', 'Natural', 'Hydrating', 'Healthy'],
  },
  'Cold Drink (Assorted)': {
    image: juiceImg,
    description:
      'Chilled assorted carbonated beverages served in original bottles — a crowd-pleasing option that guests of all ages appreciate.',
    tags: ['Beverage', 'Cold', 'Assorted'],
  },
  // Custom bank — Extras
  Raita: {
    image: friedRiceRaitaImg,
    description:
      'Chilled curd with cucumber, onion, tomato and roasted cumin — a cooling side that perfectly balances the heat of any spicy dish.',
    tags: ['Side', 'Vegetarian', 'Cooling', 'Fresh'],
  },
  Salad: {
    image: ingredientsImg,
    description:
      'Fresh seasonal salad with cucumber, tomato, onion, carrot and a lemon dressing — crisp, vitamin-rich and a light counterpoint to a heavy spread.',
    tags: ['Side', 'Vegetarian', 'Fresh', 'Healthy'],
  },
  Papad: {
    image: pickleImg,
    description:
      'Thin crispy wafers made from lentil or rice flour — roasted or fried and served as a crunchy accompaniment to any meal. Essential to every thali.',
    tags: ['Side', 'Vegetarian', 'Crunchy', 'Light'],
  },
  Pickle: {
    image: pickleImg,
    description:
      'House-made tangy pickle with seasonal vegetables or mango — the classic condiment that adds a bold punch of spice and acidity to every bite.',
    tags: ['Condiment', 'Vegetarian', 'Spicy', 'Tangy'],
  },
  'Mouth Freshener': {
    image: ingredientsImg,
    description:
      'A curated mix of saunf (fennel seeds), mishri, cardamom and mukhwas — the perfect palate-cleansing, digestive close to any meal.',
    tags: ['Post-meal', 'Digestive', 'Aromatic'],
  },
  Soup: {
    image: soupImg,
    description:
      'Hot soup — tomato, sweet corn, manchow or lemon coriander — perfect as a starter course or a warming option during cooler months.',
    tags: ['Starter', 'Vegetarian', 'Hot', 'Warming'],
  },
};

const FALLBACK_INFO = {
  image: ingredientsImg,
  description:
    'A delicious dish prepared fresh daily with authentic spices and quality ingredients by the Digirasa kitchen team.',
  tags: ['Fresh', 'Authentic'],
};

// ─── Event Types ──────────────────────────────────────────────────────────────
const EVENT_TYPES = [
  'Wedding / Engagement',
  'Birthday Celebration',
  'Corporate Event / Seminar',
  'Festival / Puja',
  'House Warming',
  'Baby Shower / Cradle Ceremony',
  'Anniversary Party',
  'Farewell / Welcome',
  'School / College Event',
  'Other',
];

const DIETARY = [
  'Pure Vegetarian',
  'Jain / No Onion-Garlic',
  'Satvik Vegetarian',
  'Vegan',
  'Low Spice Vegetarian',
  'Kids-Friendly Vegetarian',
];
const SERVICE_TYPES = [
  'Home/Venue Delivery',
  'Catering at Venue (with service staff)',
  'Self-Pickup',
];

/** Prefill name / phone / email / city / pincode from getProfile + stored auth. */
const contactFieldsFromProfileSources = ({ storedUser, profileData }) => {
  const stored = contactFromStoredUser(storedUser);
  const profileContact = contactFromProfilePayload(profileData);
  const merged = mergeCheckoutContact({ stored, profile: profileContact });
  const mapped = profileData ? profilePayloadToForm(profileData) : null;
  const first = String(mapped?.form?.first_name || '').trim();
  const last = String(mapped?.form?.last_name || '').trim();
  let firstName = first;
  let lastName = last;
  if (!firstName && !lastName && merged.fullName) {
    const parts = merged.fullName.trim().split(/\s+/);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ');
  }
  return {
    firstName,
    lastName,
    email: merged.email || '',
    mobile: merged.phone || '',
    city: String(mapped?.form?.city || '').trim() || 'Hyderabad',
    pincode: String(mapped?.form?.pincode || '').trim(),
  };
};

export default function BulkOrder() {
  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1=choose menu, 2=details+submit, 3=success

  // ── Package selection ────────────────────────────────────────────────────────
  const [selectedPackage, setSelectedPackage] = useState(null); // 'custom' or package.id
  const [menuPackageModal, setMenuPackageModal] = useState(null);

  // ── Custom menu state ────────────────────────────────────────────────────────
  const [customSelected, setCustomSelected] = useState({}); // { category: Set<item> }
  const [customText, setCustomText] = useState(''); // free-text item input
  const [customTextItems, setCustomTextItems] = useState([]); // added free-text items
  const [bankSearch, setBankSearch] = useState('');

  // ── Add-on selection (for standard packages) ─────────────────────────────────
  const [selectedAddons, setSelectedAddons] = useState({});

  // ── Customer form ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    altMobile: '',
    organization: '',
    eventType: '',
    otherEventType: '',
    eventDate: '',
    eventTime: '',
    duration: '',
    venue: '',
    city: '',
    pincode: '',
    guests: '',
    dietary: '',
    serviceType: '',
    budgetPerPlate: '',
    totalBudget: '',
    deliveryInstructions: '',
    specialRequirements: '',
    howDidYouHear: '',
  });

  const [status, setStatus] = useState(''); // '' | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [refId, setRefId] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [emailNotice, setEmailNotice] = useState('');
  const [eventDateText, setEventDateText] = useState('');
  const step1ErrorRef = useRef(null);
  const continueDetailsBtnRef = useRef(null);
  const successSectionRef = useRef(null);
  const eventDateNativeRef = useRef(null);

  /** Prefill contact info for signed-in users (event/venue fields stay blank). */
  useEffect(() => {
    let cancelled = false;

    const loadSignedInContact = async () => {
      const { token, user: storedUser } = readStoredAuth();
      if (!token) return;

      let profileData = null;
      try {
        const { res, data } = await apiGetProfile(token);
        if (res.ok) profileData = data;
      } catch {
        /* stored-user fallback */
      }
      if (cancelled) return;

      const fromProfile = contactFieldsFromProfileSources({ storedUser, profileData });
      setForm((prev) => ({
        ...prev,
        firstName: prev.firstName || fromProfile.firstName,
        lastName: prev.lastName || fromProfile.lastName,
        email: prev.email || fromProfile.email,
        mobile: prev.mobile || fromProfile.mobile,
        city: prev.city || fromProfile.city || 'Hyderabad',
        pincode: prev.pincode || fromProfile.pincode,
      }));
    };

    loadSignedInContact();
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, loadSignedInContact);
    return () => {
      cancelled = true;
      window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, loadSignedInContact);
    };
  }, []);

  useEffect(() => {
    if (step !== 3) return undefined;
    const timer = window.setTimeout(() => {
      successSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [step]);

  // ── Item / Package preview modal ─────────────────────────────────────────────
  const [previewItem, setPreviewItem] = useState(null); // { name, image, description, tags }
  const [selectedMenuDetailsOpen, setSelectedMenuDetailsOpen] = useState(false);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const loopedPackages = useMemo(() => [...PACKAGES, ...PACKAGES, ...PACKAGES], []);
  const {
    carouselAnimating,
    carouselStart,
    handleTransitionEnd,
    moveCarousel,
    visiblePackageCards,
  } = useBulkOrderCarousel({
    itemCount: PACKAGES.length,
    paused: carouselPaused,
    disabled: Boolean(menuPackageModal || previewItem || selectedMenuDetailsOpen),
  });

  const selectedMenuSummary = useMemo(() => {
    if (!selectedPackage) return null;
    if (selectedPackage === 'custom') {
      const items = [];
      Object.entries(customSelected).forEach(([category, set]) => {
        (set || new Set()).forEach((item) => items.push({ category, name: item }));
      });
      customTextItems.forEach((item) => items.push({ category: 'Custom', name: item }));
      return {
        kind: 'custom',
        title: 'Custom Menu',
        subtitle: `${items.length} item${items.length === 1 ? '' : 's'} selected`,
        items,
        addons: [],
        package: null,
      };
    }
    const pkg = PACKAGES.find((p) => p.id === selectedPackage) || null;
    if (!pkg) return null;
    const addons = Array.from(selectedAddons[selectedPackage] || []);
    return {
      kind: 'standard',
      title: pkg.name,
      subtitle: pkg.subtitle || pkg.tag || 'Standard package',
      items: (pkg.items || []).map((name) => ({ category: 'Included', name })),
      addons,
      package: pkg,
    };
  }, [selectedPackage, customSelected, customTextItems, selectedAddons]);

  const openPreview = (name, infoOverride) => {
    const info = infoOverride || ITEM_INFO[name] || FALLBACK_INFO;
    setPreviewItem({ name, ...info });
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const toggleCustomItem = (category, item) => {
    setCustomSelected((prev) => {
      const set = new Set(prev[category] || []);
      set.has(item) ? set.delete(item) : set.add(item);
      return { ...prev, [category]: set };
    });
  };

  const countCustomSelected = () =>
    Object.values(customSelected).reduce((a, s) => a + s.size, 0) + customTextItems.length;

  const addCustomTextItem = () => {
    const val = customText.trim();
    if (!val) return;
    if (!isVegCustomDish(val)) {
      setErrorMsg('Custom Menu supports vegetarian dishes only. Please add a veg item.');
      return;
    }
    if (customTextItems.includes(val)) return;
    setErrorMsg('');
    setCustomTextItems((prev) => [...prev, val]);
    setCustomText('');
  };

  const removeCustomTextItem = (item) =>
    setCustomTextItems((prev) => prev.filter((i) => i !== item));

  const toggleAddon = (pkgId, addon) => {
    setSelectedAddons((prev) => {
      const set = new Set(prev[pkgId] || []);
      set.has(addon) ? set.delete(addon) : set.add(addon);
      return { ...prev, [pkgId]: set };
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (name === 'mobile' || name === 'altMobile') {
      setForm((prev) => ({ ...prev, [name]: sanitizePhoneDigitsInput(value, 15) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const applyEventDateIso = (iso) => {
    setForm((prev) => ({ ...prev, eventDate: iso || '' }));
    setEventDateText(iso ? isoToDdMmYyyy(iso) : '');
    const dateErr = iso ? getEventDateValidationMessage(iso) : null;
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (dateErr) next.eventDate = dateErr;
      else delete next.eventDate;
      return next;
    });
  };

  const handleEventDateTextChange = (e) => {
    const raw = e.target.value;
    setEventDateText(raw);
    setFieldErrors((prev) => {
      if (!prev.eventDate) return prev;
      const next = { ...prev };
      delete next.eventDate;
      return next;
    });
    // Live-parse complete dd/mm/yyyy (or ISO) into form value
    const iso = normalizeEventDateToIso(raw);
    if (iso) {
      setForm((prev) => ({ ...prev, eventDate: iso }));
      const dateErr = getEventDateValidationMessage(iso);
      if (dateErr) {
        setFieldErrors((prev) => ({ ...prev, eventDate: dateErr }));
      }
    } else if (!raw.trim()) {
      setForm((prev) => ({ ...prev, eventDate: '' }));
    }
  };

  const handleEventDateBlur = () => {
    const raw = eventDateText.trim();
    if (!raw) {
      applyEventDateIso('');
      return;
    }
    const iso = normalizeEventDateToIso(raw);
    if (!iso) {
      setFieldErrors((prev) => ({
        ...prev,
        eventDate: 'Enter a valid event date as dd/mm/yyyy.',
      }));
      return;
    }
    applyEventDateIso(iso);
  };

  const handleEventDateNativeChange = (e) => {
    applyEventDateIso(e.target.value || '');
  };

  const openEventDatePicker = () => {
    const el = eventDateNativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // fall through to focus/click
      }
    }
    el.focus();
    el.click();
  };

  const scrollToStep1Error = () => {
    if (step1ErrorRef.current) {
      step1ErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const focusField = (fieldName) => {
    window.requestAnimationFrame(() => {
      const root = document.querySelector('.bulk-form');
      if (!root) return;
      const el = root.querySelector(`[name="${fieldName}"]`);
      if (!el) return;
      if (typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const setFieldError = (fieldName, message) => {
    setErrorMsg('');
    setFieldErrors({ [fieldName]: message });
    window.setTimeout(() => focusField(fieldName), 50);
  };

  const fieldGroupClass = (fieldName) =>
    `form-group${fieldErrors[fieldName] ? ' form-group--error' : ''}`;

  // ── Validation ───────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    if (!selectedPackage) return 'Please select a menu package or choose Custom Menu.';
    if (selectedPackage === 'custom' && countCustomSelected() < 3)
      return 'Please select at least 3 items for your custom menu.';
    return null;
  };

  /** Validate step 2 in on-screen field order; returns { field, message } or null. */
  const validateStep2 = () => {
    if (!form.firstName.trim()) return { field: 'firstName', message: 'First name is required.' };
    if (!form.email.trim()) return { field: 'email', message: 'Email address is required.' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return { field: 'email', message: 'Enter a valid email address.' };
    }
    const mobileCheck = validatePhoneDigitsValue(form.mobile);
    if (!mobileCheck.ok) return { field: 'mobile', message: mobileCheck.message };
    if (form.altMobile.trim()) {
      const altCheck = validatePhoneDigitsValue(form.altMobile, { optional: true });
      if (!altCheck.ok) return { field: 'altMobile', message: altCheck.message };
    }
    if (!form.eventType) return { field: 'eventType', message: 'Please select an event type.' };
    if (form.eventType === 'Other' && !form.otherEventType.trim()) {
      return { field: 'otherEventType', message: 'Please describe your event.' };
    }
    if (!form.eventDate) return { field: 'eventDate', message: 'Event date is required.' };
    const eventDateErr = getEventDateValidationMessage(form.eventDate);
    if (eventDateErr) return { field: 'eventDate', message: eventDateErr };
    if (!form.eventTime) return { field: 'eventTime', message: 'Event time is required.' };
    if (!form.duration) return { field: 'duration', message: 'Event duration is required.' };
    if (!form.guests || parseInt(form.guests, 10) < 20) {
      return { field: 'guests', message: 'Minimum 20 pax required.' };
    }
    if (!form.venue.trim()) return { field: 'venue', message: 'Venue / address is required.' };
    if (!form.dietary) {
      return { field: 'dietary', message: 'Please select a dietary preference.' };
    }
    if (!form.serviceType) {
      return { field: 'serviceType', message: 'Please select a service type.' };
    }
    if (!String(form.budgetPerPlate || '').trim()) {
      return { field: 'budgetPerPlate', message: 'Budget per plate is required.' };
    }
    const perPlate = Number(form.budgetPerPlate);
    if (!Number.isFinite(perPlate) || perPlate < 100) {
      return { field: 'budgetPerPlate', message: 'Enter a valid budget per plate (min ₹100).' };
    }
    if (!String(form.totalBudget || '').trim()) {
      return { field: 'totalBudget', message: 'Total budget is required.' };
    }
    const total = Number(form.totalBudget);
    if (!Number.isFinite(total) || total < 1000) {
      return { field: 'totalBudget', message: 'Enter a valid total budget (min ₹1000).' };
    }
    if (!form.howDidYouHear) {
      return { field: 'howDidYouHear', message: 'Please select how you heard about us.' };
    }
    return null;
  };

  const handleStep1Next = () => {
    const err = validateStep1();
    if (err) {
      setErrorMsg(err);
      scrollToStep1Error();
      return;
    }
    setErrorMsg('');
    setFieldErrors({});
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep2();
    if (err) {
      setFieldError(err.field, err.message);
      return;
    }
    setFieldErrors({});
    setErrorMsg('');
    setStatus('loading');

    // Build payload
    let menuDetails;
    if (selectedPackage === 'custom') {
      const chosen = [];
      Object.entries(customSelected).forEach(([cat, set]) => {
        set.forEach((item) => chosen.push(`[${cat}] ${item}`));
      });
      customTextItems.forEach((item) => chosen.push(`[Custom] ${item}`));
      menuDetails = { type: 'custom', items: chosen };
    } else {
      const pkg = PACKAGES.find((p) => p.id === selectedPackage);
      menuDetails = {
        type: 'standard',
        packageName: pkg.name,
        packageItems: pkg.items,
        addons: Array.from(selectedAddons[selectedPackage] || []),
      };
    }

    const id = genRefId();

    const payload = {
      ...form,
      city: 'Hyderabad',
      eventTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
      menuDetails,
      referenceId: id,
    };

    try {
      const data = await createBulkEnquiry({
        payload,
        customerName: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
        customerEmail: form.email || '',
        customerPhone: form.mobile || '',
      });
      const referenceId = data?.enquiry?.payload?.referenceId || id;
      const nextLookupCode = data?.lookupCode || '';
      let nextEmailNotice = '';
      if (!data?.customerEmailDelivered) {
        nextEmailNotice = data?.opsEmailDelivered
          ? 'Your request was saved and our team was notified, but the confirmation email to you could not be sent. Please check spam or contact customercare@digirasa.com with your reference ID.'
          : 'Your request was saved, but confirmation emails could not be sent right now. Please keep your reference ID and contact customercare@digirasa.com if needed.';
      }
      setRefId(referenceId);
      setLookupCode(nextLookupCode);
      setEmailNotice(nextEmailNotice);
      setStatus('success');
      setStep(3);
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMsg(err.message || 'Submission failed. Please try again.');
      } else {
        setErrorMsg(err?.message || 'Network error. Please check your connection and try again.');
      }
      setStatus('error');
    }
  };

  // ── Filtered bank items ───────────────────────────────────────────────────────
  const filteredBank = bankSearch.trim()
    ? Object.entries(MENU_BANK).reduce((acc, [cat, items]) => {
        const filtered = items.filter((i) => i.toLowerCase().includes(bankSearch.toLowerCase()));
        if (filtered.length) acc[cat] = filtered;
        return acc;
      }, {})
    : MENU_BANK;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="bulk-order">
      {/* ── HERO ── */}
      <section className="bulk-hero">
        <div className="bulk-hero__overlay" />
        <div className="container bulk-hero__content">
          <span className="bulk-hero__badge">
            <FaConciergeBell /> Bulk &amp; Event Catering
          </span>
          <h1 className="bulk-hero__title">Plan Your Perfect Event</h1>
          <p className="bulk-hero__sub">
            Authentic Digirasa flavours served at scale — weddings, corporates, celebrations &amp;
            more. Minimum 20 pax · Hyderabad &amp; surroundings.
          </p>
          <div className="bulk-hero__stats">
            <div className="bulk-hero__stat">
              <span>500+</span>
              <small>Events Catered</small>
            </div>
            <div className="bulk-hero__stat">
              <span>10K+</span>
              <small>Happy Guests</small>
            </div>
            <div className="bulk-hero__stat">
              <span>48h</span>
              <small>Advance Notice</small>
            </div>
            <div className="bulk-hero__stat">
              <span>100%</span>
              <small>Fresh &amp; Hygienic</small>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROGRESS ── */}
      <div className="bulk-progress">
        <div className="container bulk-progress__inner">
          {[
            { n: 1, label: 'Choose Menu' },
            { n: 2, label: 'Your Details' },
            { n: 3, label: 'Confirmation' },
          ].map((s) => (
            <div
              key={s.n}
              className={`bulk-progress__step${step >= s.n ? ' active' : ''}${step === s.n ? ' current' : ''}`}
            >
              <div className="bulk-progress__bubble">{step > s.n ? <FaCheckCircle /> : s.n}</div>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="container bulk-main">
        {/* ════════════════════════════════════════════════
            STEP 1 — MENU SELECTION
        ════════════════════════════════════════════════ */}
        {step === 1 && (
          <section className="bulk-step bulk-step--menu-select">
            <h2 className="bulk-section-title">Select Your Event Menu</h2>
            <p className="bulk-section-sub">
              Choose one of our curated event packages or build your own custom menu.
            </p>

            {errorMsg && (
              <div ref={step1ErrorRef} className="bulk-alert bulk-alert--error">
                {errorMsg}
              </div>
            )}

            {/* ── Standard packages ── */}
            <div
              className="bulk-packages-carousel"
              onMouseEnter={() => setCarouselPaused(true)}
              onMouseLeave={() => setCarouselPaused(false)}
              style={{ '--visible-cards': visiblePackageCards }}
            >
              <button
                type="button"
                className="bulk-packages-carousel__nav bulk-packages-carousel__nav--left"
                aria-label="Previous package cards"
                onClick={() => moveCarousel(-1)}
              >
                <FaChevronLeft />
              </button>

              <div className="bulk-packages-viewport">
                <div
                  className="bulk-packages-track"
                  style={{
                    transform: `translateX(-${carouselStart * (100 / visiblePackageCards)}%)`,
                    transition: carouselAnimating ? undefined : 'none',
                  }}
                  onTransitionEnd={handleTransitionEnd}
                  onWheel={(e) => {
                    e.preventDefault();
                    if (e.deltaY > 0) {
                      moveCarousel(1);
                    } else if (e.deltaY < 0) {
                      moveCarousel(-1);
                    }
                  }}
                >
                  {loopedPackages.map((pkg, index) => (
                    <div className="bulk-packages-slide" key={`${pkg.id}-${index}`}>
                      <div
                        className={`bulk-pkg${pkg.featured ? ' bulk-pkg--featured' : ''} bulk-pkg--${pkg.color}${selectedPackage === pkg.id ? ' selected' : ''}`}
                        onClick={() => {
                          setSelectedPackage(pkg.id);
                          setErrorMsg('');
                          continueDetailsBtnRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                        }}
                      >
                        {pkg.featured && <span className="bulk-pkg__ribbon">★ MOST POPULAR</span>}
                        {pkg.image && (
                          <div className="bulk-pkg__img-strip">
                            <img src={pkg.image} alt={pkg.name} />
                            <button
                              className="bulk-pkg__img-info-btn"
                              type="button"
                              title="About this package"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPreview(pkg.name, {
                                  image: pkg.image,
                                  description: pkg.description,
                                  tags: pkg.tags,
                                });
                              }}
                            >
                              <FaInfoCircle /> About this package
                            </button>
                          </div>
                        )}
                        <div className="bulk-pkg__header">
                          <div className="bulk-pkg__icon">{pkg.icon}</div>
                          <div>
                            <span className="bulk-pkg__tag">{pkg.tag}</span>
                            <h3 className="bulk-pkg__name">{pkg.name}</h3>
                            <p className="bulk-pkg__sub">{pkg.subtitle}</p>
                          </div>
                          <div
                            className={`bulk-pkg__radio${selectedPackage === pkg.id ? ' checked' : ''}`}
                          />
                        </div>

                        <div className="bulk-pkg__price">
                          From ₹{Math.min(...pkg.pricing.map((p) => p.price))}/plate
                        </div>
                        <div className="bulk-pkg__guests">Minimum {pkg.minGuests} pax</div>
                        <div className="bulk-gst-notice" role="note" aria-label="Pricing note">
                          <FaInfoCircle className="bulk-gst-notice__icon" />
                          <div className="bulk-gst-notice__content">
                            <span>GST &amp; Delivery charges extra</span>
                          </div>
                        </div>

                        <button
                          className="bulk-pkg__toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPackage(pkg.id);
                            setMenuPackageModal(pkg);
                          }}
                          type="button"
                        >
                          <FaClipboardList /> View Full Menu
                        </button>

                        {/* Add-ons */}
                        {selectedPackage === pkg.id && pkg.addons?.length > 0 && (
                          <div className="bulk-pkg__addons" onClick={(e) => e.stopPropagation()}>
                            <p className="bulk-pkg__addons-label">Optional Add-ons:</p>
                            <div className="bulk-pkg__addons-list">
                              {pkg.addons.map((addon) => (
                                <label key={addon} className="bulk-addon-chip">
                                  <input
                                    type="checkbox"
                                    checked={(selectedAddons[pkg.id] || new Set()).has(addon)}
                                    onChange={() => toggleAddon(pkg.id, addon)}
                                  />
                                  {addon}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="bulk-packages-carousel__nav bulk-packages-carousel__nav--right"
                aria-label="Next package cards"
                onClick={() => moveCarousel(1)}
              >
                <FaChevronRight />
              </button>
            </div>

            {/* ── Divider ── */}
            <div className="bulk-or-divider">
              <span>OR BUILD YOUR OWN</span>
            </div>

            {/* ── Custom Menu ── */}
            <div
              className={`bulk-custom-panel${selectedPackage === 'custom' ? ' selected' : ''}`}
              onClick={() => setSelectedPackage('custom')}
            >
              <div className="bulk-custom-panel__header">
                <FaClipboardList className="bulk-custom-icon" />
                <div>
                  <h3>Custom Menu</h3>
                  <p>
                    Pick exactly what you want from our pure veg item bank, or add your own
                    vegetarian dishes by typing.
                  </p>
                </div>
                <div
                  className={`bulk-pkg__radio${selectedPackage === 'custom' ? ' checked' : ''}`}
                />
              </div>

              {selectedPackage === 'custom' && (
                <div className="bulk-custom-body" onClick={(e) => e.stopPropagation()}>
                  {/* Search */}
                  <div className="bulk-custom-search">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search dishes…"
                      value={bankSearch}
                      onChange={(e) => setBankSearch(e.target.value)}
                    />
                  </div>

                  {/* Item bank */}
                  {Object.entries(filteredBank).map(([category, items]) => (
                    <div key={category} className="bulk-bank-category">
                      <h4 className="bulk-bank-cat-title">{category}</h4>
                      <div className="bulk-bank-items">
                        {items.map((item) => {
                          const checked = (customSelected[category] || new Set()).has(item);
                          return (
                            <div key={item} className="bulk-item-chip-wrap">
                              <label className={`bulk-item-chip${checked ? ' selected' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCustomItem(category, item)}
                                />
                                {item}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Type-in custom item */}
                  <div className="bulk-custom-type">
                    <h4>Add a vegetarian custom dish not listed above</h4>
                    <div className="bulk-custom-type__row">
                      <input
                        type="text"
                        placeholder="e.g. Grandma's Special Khichdi, Veg Pulao…"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && (e.preventDefault(), addCustomTextItem())
                        }
                        maxLength={120}
                      />
                      <button
                        type="button"
                        onClick={addCustomTextItem}
                        className="btn btn--primary bulk-custom-add-btn"
                      >
                        <FaPlusCircle /> Add
                      </button>
                    </div>
                    {customTextItems.length > 0 && (
                      <div className="bulk-custom-typed-list">
                        {customTextItems.map((it) => (
                          <span key={it} className="bulk-typed-chip">
                            {it}
                            <button type="button" onClick={() => removeCustomTextItem(it)}>
                              <FaTrash />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  {countCustomSelected() > 0 && (
                    <div className="bulk-custom-summary">
                      <FaCheckCircle /> {countCustomSelected()} item
                      {countCustomSelected() !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bulk-step__actions">
              <button
                ref={continueDetailsBtnRef}
                className="btn btn--primary bulk-next-btn"
                onClick={handleStep1Next}
              >
                Continue to Details →
              </button>
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════════
            STEP 2 — CUSTOMER DETAILS FORM
        ════════════════════════════════════════════════ */}
        {step === 2 && (
          <section className="bulk-step bulk-step--details">
            <h2 className="bulk-section-title">Your Event &amp; Contact Details</h2>
            <p className="bulk-section-sub">
              Fill in the details below and our team will reach out within 24 hours to confirm your
              booking.
            </p>
            <div className="bulk-step__top-actions">
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setStep(1);
                  setErrorMsg('');
                  setFieldErrors({});
                  setSelectedMenuDetailsOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                ← Back to Menu Selection
              </button>
            </div>

            {selectedMenuSummary ? (
              <div className="bulk-selected-menu">
                <div className="bulk-selected-menu__copy">
                  <span className="bulk-selected-menu__label">Selected menu</span>
                  <strong className="bulk-selected-menu__title">{selectedMenuSummary.title}</strong>
                  <span className="bulk-selected-menu__sub">{selectedMenuSummary.subtitle}</span>
                  {selectedMenuSummary.kind === 'standard' && selectedMenuSummary.addons.length > 0 ? (
                    <span className="bulk-selected-menu__addons">
                      + {selectedMenuSummary.addons.length} add-on
                      {selectedMenuSummary.addons.length === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="bulk-selected-menu__view-btn"
                  onClick={() => setSelectedMenuDetailsOpen(true)}
                >
                  <FaClipboardList aria-hidden /> View menu details
                </button>
              </div>
            ) : null}

            {errorMsg ? (
              <div className="bulk-alert bulk-alert--error" role="alert">
                {errorMsg}
              </div>
            ) : null}

            <form className="bulk-form" onSubmit={handleSubmit} noValidate>
              {/* ── Contact Information ── */}
              <fieldset className="bulk-fieldset">
                <legend>Contact Information</legend>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('firstName')}>
                    <label htmlFor="bo-firstName">First Name *</label>
                    <input
                      id="bo-firstName"
                      name="firstName"
                      type="text"
                      value={form.firstName}
                      onChange={handleFormChange}
                      placeholder="Your first name"
                      maxLength={50}
                      aria-invalid={Boolean(fieldErrors.firstName)}
                    />
                    {fieldErrors.firstName ? <p className="bulk-field-error">{fieldErrors.firstName}</p> : null}
                  </div>
                  <div className={fieldGroupClass('lastName')}>
                    <label htmlFor="bo-lastName">Last Name</label>
                    <input
                      id="bo-lastName"
                      name="lastName"
                      type="text"
                      value={form.lastName}
                      onChange={handleFormChange}
                      placeholder="Optional"
                      maxLength={50}
                      aria-invalid={Boolean(fieldErrors.lastName)}
                    />
                    {fieldErrors.lastName ? <p className="bulk-field-error">{fieldErrors.lastName}</p> : null}
                  </div>
                </div>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('email')}>
                    <label htmlFor="bo-email">Email Address *</label>
                    <input
                      id="bo-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      placeholder="you@example.com"
                      aria-invalid={Boolean(fieldErrors.email)}
                    />
                    {fieldErrors.email ? <p className="bulk-field-error">{fieldErrors.email}</p> : null}
                  </div>
                  <div className={fieldGroupClass('mobile')}>
                    <label htmlFor="bo-mobile">Mobile Number *</label>
                    <input
                      id="bo-mobile"
                      name="mobile"
                      type="tel"
                      inputMode="numeric"
                      value={form.mobile}
                      onChange={handleFormChange}
                      placeholder="10-digit or with country code"
                      maxLength={15}
                      aria-invalid={Boolean(fieldErrors.mobile)}
                    />
                    {fieldErrors.mobile ? <p className="bulk-field-error">{fieldErrors.mobile}</p> : null}
                  </div>
                </div>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('altMobile')}>
                    <label htmlFor="bo-altMobile">Alternate Mobile</label>
                    <input
                      id="bo-altMobile"
                      name="altMobile"
                      type="tel"
                      inputMode="numeric"
                      value={form.altMobile}
                      onChange={handleFormChange}
                      placeholder="Optional"
                      maxLength={15}
                      aria-invalid={Boolean(fieldErrors.altMobile)}
                    />
                    {fieldErrors.altMobile ? <p className="bulk-field-error">{fieldErrors.altMobile}</p> : null}
                  </div>
                  <div className="form-group">
                    <label htmlFor="bo-organization">Organization / Company</label>
                    <input
                      id="bo-organization"
                      name="organization"
                      type="text"
                      value={form.organization}
                      onChange={handleFormChange}
                      placeholder="Optional (for corporate events)"
                      maxLength={100}
                    />
                  </div>
                </div>
              </fieldset>

              {/* ── Event Information ── */}
              <fieldset className="bulk-fieldset">
                <legend>Event Information</legend>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('eventType')}>
                    <label htmlFor="bo-eventType">Event Type *</label>
                    <select
                      id="bo-eventType"
                      name="eventType"
                      value={form.eventType}
                      onChange={handleFormChange}
                      aria-invalid={Boolean(fieldErrors.eventType)}
                    >
                      <option value="">Select event type…</option>
                      {EVENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.eventType ? <p className="bulk-field-error">{fieldErrors.eventType}</p> : null}
                  </div>
                  {form.eventType === 'Other' && (
                    <div className={fieldGroupClass('otherEventType')}>
                      <label htmlFor="bo-otherEventType">Describe Your Event *</label>
                      <input
                        id="bo-otherEventType"
                        name="otherEventType"
                        type="text"
                        value={form.otherEventType}
                        onChange={handleFormChange}
                        placeholder="e.g. Reunion Dinner"
                        maxLength={100}
                        aria-invalid={Boolean(fieldErrors.otherEventType)}
                      />
                      {fieldErrors.otherEventType ? (
                        <p className="bulk-field-error">{fieldErrors.otherEventType}</p>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('eventDate')}>
                    <label htmlFor="bo-eventDate">Event Date *</label>
                    <div className="bulk-date-input">
                      <input
                        id="bo-eventDate"
                        name="eventDate"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="dd/mm/yyyy"
                        value={eventDateText}
                        onChange={handleEventDateTextChange}
                        onBlur={handleEventDateBlur}
                        aria-invalid={Boolean(fieldErrors.eventDate)}
                      />
                      <input
                        ref={eventDateNativeRef}
                        type="date"
                        className="bulk-date-input__native"
                        value={form.eventDate}
                        min={getMinEventDate()}
                        tabIndex={-1}
                        aria-hidden="true"
                        onChange={handleEventDateNativeChange}
                      />
                      <button
                        type="button"
                        className="bulk-date-input__calendar"
                        onClick={openEventDatePicker}
                        aria-label="Open calendar"
                      >
                        <FaCalendarAlt aria-hidden />
                      </button>
                    </div>
                    {fieldErrors.eventDate ? <p className="bulk-field-error">{fieldErrors.eventDate}</p> : null}
                  </div>
                  <div className={fieldGroupClass('eventTime')}>
                    <label htmlFor="bo-eventTime">Event Time *</label>
                    <input
                      id="bo-eventTime"
                      name="eventTime"
                      type="time"
                      value={form.eventTime}
                      onChange={handleFormChange}
                      aria-invalid={Boolean(fieldErrors.eventTime)}
                    />
                    {fieldErrors.eventTime ? <p className="bulk-field-error">{fieldErrors.eventTime}</p> : null}
                  </div>
                </div>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('duration')}>
                    <label htmlFor="bo-duration">Event Duration *</label>
                    <select
                      id="bo-duration"
                      name="duration"
                      value={form.duration}
                      onChange={handleFormChange}
                      aria-invalid={Boolean(fieldErrors.duration)}
                    >
                      <option value="">Select…</option>
                      <option>1 – 2 hours</option>
                      <option>2 – 4 hours</option>
                      <option>4 – 6 hours</option>
                      <option>Full Day</option>
                      <option>Multi-Day</option>
                    </select>
                    {fieldErrors.duration ? <p className="bulk-field-error">{fieldErrors.duration}</p> : null}
                  </div>
                  <div className={fieldGroupClass('guests')}>
                    <label htmlFor="bo-guests">Number of Pax *</label>
                    <input
                      id="bo-guests"
                      name="guests"
                      type="number"
                      value={form.guests}
                      onChange={handleFormChange}
                      placeholder="Min 20"
                      min={20}
                      max={5000}
                      aria-invalid={Boolean(fieldErrors.guests)}
                    />
                    {fieldErrors.guests ? <p className="bulk-field-error">{fieldErrors.guests}</p> : null}
                  </div>
                </div>
              </fieldset>

              {/* ── Venue & Location ── */}
              <fieldset className="bulk-fieldset">
                <legend>Venue &amp; Location</legend>
                <div className={fieldGroupClass('venue')}>
                  <label htmlFor="bo-venue">Venue / Full Address *</label>
                  <textarea
                    id="bo-venue"
                    name="venue"
                    rows={2}
                    value={form.venue}
                    onChange={handleFormChange}
                    placeholder="Hall name, building, street…"
                    maxLength={300}
                    aria-invalid={Boolean(fieldErrors.venue)}
                  />
                  {fieldErrors.venue ? <p className="bulk-field-error">{fieldErrors.venue}</p> : null}
                </div>
                <div className="bulk-form__row">
                  <div className="form-group">
                    <label htmlFor="bo-city">City</label>
                    <input id="bo-city" name="city" type="text" value="Hyderabad" readOnly />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bo-pincode">Pincode</label>
                    <input
                      id="bo-pincode"
                      name="pincode"
                      type="text"
                      value={form.pincode}
                      onChange={handleFormChange}
                      placeholder="xxxxxx"
                      maxLength={10}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bo-deliveryInstructions">Delivery / Access Instructions</label>
                  <textarea
                    id="bo-deliveryInstructions"
                    name="deliveryInstructions"
                    rows={2}
                    value={form.deliveryInstructions}
                    onChange={handleFormChange}
                    placeholder="e.g. Gate 2, parking near south entrance…"
                    maxLength={300}
                  />
                </div>
              </fieldset>

              {/* ── Preferences & Budget ── */}
              <fieldset className="bulk-fieldset">
                <legend>Preferences &amp; Budget</legend>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('dietary')}>
                    <label htmlFor="bo-dietary">Dietary Preference *</label>
                    <select
                      id="bo-dietary"
                      name="dietary"
                      value={form.dietary}
                      onChange={handleFormChange}
                      aria-invalid={Boolean(fieldErrors.dietary)}
                    >
                      <option value="">Select…</option>
                      {DIETARY.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.dietary ? <p className="bulk-field-error">{fieldErrors.dietary}</p> : null}
                  </div>
                  <div className={fieldGroupClass('serviceType')}>
                    <label htmlFor="bo-serviceType">Service Type *</label>
                    <select
                      id="bo-serviceType"
                      name="serviceType"
                      value={form.serviceType}
                      onChange={handleFormChange}
                      aria-invalid={Boolean(fieldErrors.serviceType)}
                    >
                      <option value="">Select…</option>
                      {SERVICE_TYPES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.serviceType ? (
                      <p className="bulk-field-error">{fieldErrors.serviceType}</p>
                    ) : null}
                  </div>
                </div>
                <div className="bulk-form__row">
                  <div className={fieldGroupClass('budgetPerPlate')}>
                    <label htmlFor="bo-budgetPerPlate">Budget per Plate (₹) *</label>
                    <input
                      id="bo-budgetPerPlate"
                      name="budgetPerPlate"
                      type="number"
                      value={form.budgetPerPlate}
                      onChange={handleFormChange}
                      placeholder="e.g. 250"
                      min={100}
                      max={5000}
                      aria-invalid={Boolean(fieldErrors.budgetPerPlate)}
                    />
                    {fieldErrors.budgetPerPlate ? (
                      <p className="bulk-field-error">{fieldErrors.budgetPerPlate}</p>
                    ) : null}
                  </div>
                  <div className={fieldGroupClass('totalBudget')}>
                    <label htmlFor="bo-totalBudget">Total Budget (₹) *</label>
                    <input
                      id="bo-totalBudget"
                      name="totalBudget"
                      type="number"
                      value={form.totalBudget}
                      onChange={handleFormChange}
                      placeholder="e.g. 25000"
                      min={1000}
                      aria-invalid={Boolean(fieldErrors.totalBudget)}
                    />
                    {fieldErrors.totalBudget ? (
                      <p className="bulk-field-error">{fieldErrors.totalBudget}</p>
                    ) : null}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bo-specialRequirements">Special Requirements / Notes</label>
                  <textarea
                    id="bo-specialRequirements"
                    name="specialRequirements"
                    rows={3}
                    value={form.specialRequirements}
                    onChange={handleFormChange}
                    placeholder="Allergy notes, extra chairs, live counter, stage setup, theme decoration, timing flexibility…"
                    maxLength={1000}
                  />
                </div>
                <div className={fieldGroupClass('howDidYouHear')}>
                  <label htmlFor="bo-howDidYouHear">How did you hear about us? *</label>
                  <select
                    id="bo-howDidYouHear"
                    name="howDidYouHear"
                    value={form.howDidYouHear}
                    onChange={handleFormChange}
                    aria-invalid={Boolean(fieldErrors.howDidYouHear)}
                  >
                    <option value="">Select…</option>
                    <option>Google Search</option>
                    <option>Instagram / Facebook</option>
                    <option>Friend / Family Referral</option>
                    <option>Previous Customer</option>
                    <option>Digirasa App</option>
                    <option>Newspaper / Flyer</option>
                    <option>Other</option>
                  </select>
                  {fieldErrors.howDidYouHear ? (
                    <p className="bulk-field-error">{fieldErrors.howDidYouHear}</p>
                  ) : null}
                </div>
              </fieldset>

              <div className="bulk-step__actions">
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => {
                    setStep(1);
                    setErrorMsg('');
                    setFieldErrors({});
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  className="btn btn--primary bulk-next-btn"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'Submitting…' : 'Submit Bulk Order Request →'}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* ════════════════════════════════════════════════
            STEP 3 — SUCCESS / CONFIRMATION
        ════════════════════════════════════════════════ */}
        {step === 3 && (
          <section
            ref={successSectionRef}
            className="bulk-step bulk-success bulk-step--confirmation"
            tabIndex={-1}
            aria-live="polite"
          >
            <div className="bulk-success__icon">
              <FaCheckCircle />
            </div>
            <h2>Request Received!</h2>
            <p>
              Thank you, <strong>{form.firstName}</strong>! Your bulk order request has been
              submitted successfully. Our team at <strong>customercare@digirasa.com</strong> will
              review your requirements and contact you at <strong>{form.mobile}</strong> within{' '}
              <strong>24 hours</strong>.
            </p>
            <div className="bulk-success__ref">
              <span>Your Reference ID</span>
              <strong>{refId}</strong>
              {lookupCode ? (
                <small>
                  Lookup code: <strong>{lookupCode}</strong>
                </small>
              ) : null}
              <small>
                View this request anytime under{' '}
                <Link to="/account/bulk-orders">My Profile → Bulk Orders</Link>.
              </small>
            </div>
            {emailNotice ? (
              <p className="bulk-alert bulk-alert--error" role="status">
                {emailNotice}
              </p>
            ) : (
              <p className="bulk-alert bulk-alert--success" role="status">
                A confirmation email was sent to <strong>{form.email}</strong>, and our customer
                service team was notified.
              </p>
            )}
            <div className="bulk-success__next">
              <h4>What happens next?</h4>
              <ol>
                <li>Our catering coordinator will call you to discuss menu &amp; logistics.</li>
                <li>You'll receive a detailed quote via email within 24 hours.</li>
                <li>On approval, a 30% advance confirms your booking.</li>
                <li>Fresh food delivered / served on your event day!</li>
              </ol>
            </div>
            <div className="bulk-success__contact">
              <a href="tel:+918143231857">
                <FaPhone /> +91 81432 31857
              </a>
              <a href="mailto:customercare@digirasa.com">
                <FaEnvelope /> customercare@digirasa.com
              </a>
            </div>
            <button
              className="btn btn--primary"
              onClick={() => {
                setStep(1);
                setSelectedPackage(null);
                setLookupCode('');
                setEmailNotice('');
                setStatus('');
                setErrorMsg('');
                setFieldErrors({});
                setEventDateText('');
                setForm((f) => ({
                  ...f,
                  altMobile: '',
                  organization: '',
                  eventType: '',
                  otherEventType: '',
                  eventDate: '',
                  eventTime: '',
                  duration: '',
                  venue: '',
                  guests: '',
                  dietary: '',
                  serviceType: '',
                  budgetPerPlate: '',
                  totalBudget: '',
                  deliveryInstructions: '',
                  specialRequirements: '',
                  howDidYouHear: '',
                  // keep firstName/lastName/email/mobile/city/pincode from signed-in profile
                }));
              }}
            >
              Submit Another Request
            </button>
          </section>
        )}
      </div>

      {/* ── Package Menu Modal ── */}
      {menuPackageModal && (
        <div
          className="item-modal-overlay"
          onClick={() => setMenuPackageModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label={menuPackageModal.name}
        >
          <div
            className={`item-modal bulk-menu-modal bulk-menu-modal--${menuPackageModal.color || 'orange'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="item-modal__close"
              onClick={() => setMenuPackageModal(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="bulk-menu-modal__header">
              <span
                className={`bulk-pkg__tag bulk-menu-modal__ribbon bulk-menu-modal__ribbon--${menuPackageModal.color || 'orange'}`}
              >
                {menuPackageModal.tag}
              </span>
              <h3 className="item-modal__name">{menuPackageModal.name}</h3>
              <p className="bulk-menu-modal__sub">{menuPackageModal.subtitle}</p>
            </div>
            <div className="item-modal__body bulk-menu-modal__body">
              <p className="item-modal__desc">{menuPackageModal.description}</p>

              <div className="bulk-menu-modal__section bulk-menu-modal__section--pricing">
                <p className="bulk-pkg__pricing-title">
                  <FaTag /> Price list
                </p>
                <div className="bulk-price-grid">
                  {[
                    {
                      label: 'Up to 50 Plates',
                      price:
                        menuPackageModal.pricing.find((tier) => tier.plates === 50)?.price ??
                        menuPackageModal.pricing.find((tier) => tier.plates <= 50)?.price ??
                        menuPackageModal.pricing[0]?.price,
                    },
                    {
                      label: 'Up to 100 Plates',
                      price:
                        menuPackageModal.pricing.find((tier) => tier.plates === 100)?.price ??
                        menuPackageModal.pricing[menuPackageModal.pricing.length - 1]?.price,
                    },
                  ]
                    .filter((tier) => Number.isFinite(tier.price))
                    .map((tier) => (
                      <div key={tier.label} className="bulk-price-card">
                        <span className="bulk-price-card__plates">{tier.label}</span>
                        <strong className="bulk-price-card__value">
                          ₹{tier.price}
                          <small>/plate</small>
                        </strong>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bulk-menu-modal__section">
                <p className="bulk-pkg__pricing-title">
                  <FaClipboardList /> Included menu items
                </p>
                <ul className="bulk-pkg__items bulk-pkg__items--modal">
                  {menuPackageModal.items.map((it) => (
                    <li key={it}>
                      <FaCheckCircle className="check-icon" />
                      <span className="bulk-pkg__item-name">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bulk-menu-modal__actions">
              <button
                type="button"
                className="btn btn--primary bulk-menu-modal__select-btn"
                onClick={() => {
                  setSelectedPackage(menuPackageModal.id);
                  setMenuPackageModal(null);
                  setErrorMsg('');
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Select This Package
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Selected menu details (step 2) ── */}
      {selectedMenuDetailsOpen && selectedMenuSummary ? (
        <div
          className="item-modal-overlay"
          onClick={() => setSelectedMenuDetailsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-selected-menu-details-title"
        >
          <div
            className={`item-modal bulk-menu-modal bulk-menu-modal--${selectedMenuSummary.package?.color || 'orange'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="item-modal__close"
              onClick={() => setSelectedMenuDetailsOpen(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="bulk-menu-modal__header">
              {selectedMenuSummary.package?.tag ? (
                <span
                  className={`bulk-pkg__tag bulk-menu-modal__ribbon bulk-menu-modal__ribbon--${selectedMenuSummary.package.color || 'orange'}`}
                >
                  {selectedMenuSummary.package.tag}
                </span>
              ) : (
                <span className="bulk-pkg__tag bulk-menu-modal__ribbon bulk-menu-modal__ribbon--orange">
                  CUSTOM
                </span>
              )}
              <h3 id="bulk-selected-menu-details-title" className="item-modal__name">
                {selectedMenuSummary.title}
              </h3>
              <p className="bulk-menu-modal__sub">{selectedMenuSummary.subtitle}</p>
            </div>
            <div className="item-modal__body bulk-menu-modal__body">
              {selectedMenuSummary.package?.description ? (
                <p className="item-modal__desc">{selectedMenuSummary.package.description}</p>
              ) : (
                <p className="item-modal__desc">
                  Your custom vegetarian menu selection for this bulk order enquiry.
                </p>
              )}

              {selectedMenuSummary.kind === 'standard' && selectedMenuSummary.package?.pricing ? (
                <div className="bulk-menu-modal__section bulk-menu-modal__section--pricing">
                  <p className="bulk-pkg__pricing-title">
                    <FaTag /> Price list
                  </p>
                  <div className="bulk-price-grid">
                    {[
                      {
                        label: 'Up to 50 Plates',
                        price:
                          selectedMenuSummary.package.pricing.find((tier) => tier.plates === 50)?.price
                          ?? selectedMenuSummary.package.pricing.find((tier) => tier.plates <= 50)?.price
                          ?? selectedMenuSummary.package.pricing[0]?.price,
                      },
                      {
                        label: 'Up to 100 Plates',
                        price:
                          selectedMenuSummary.package.pricing.find((tier) => tier.plates === 100)?.price
                          ?? selectedMenuSummary.package.pricing[
                            selectedMenuSummary.package.pricing.length - 1
                          ]?.price,
                      },
                    ]
                      .filter((tier) => Number.isFinite(tier.price))
                      .map((tier) => (
                        <div key={tier.label} className="bulk-price-card">
                          <span className="bulk-price-card__plates">{tier.label}</span>
                          <strong className="bulk-price-card__value">
                            ₹{tier.price}
                            <small>/plate</small>
                          </strong>
                        </div>
                      ))}
                  </div>
                </div>
              ) : null}

              <div className="bulk-menu-modal__section">
                <p className="bulk-pkg__pricing-title">
                  <FaClipboardList />{' '}
                  {selectedMenuSummary.kind === 'custom' ? 'Selected dishes' : 'Included menu items'}
                </p>
                {selectedMenuSummary.items.length === 0 ? (
                  <p className="bulk-selected-menu-empty">No items selected.</p>
                ) : (
                  <ul className="bulk-pkg__items bulk-pkg__items--modal">
                    {selectedMenuSummary.items.map((it) => (
                      <li key={`${it.category}-${it.name}`}>
                        <FaCheckCircle className="check-icon" />
                        <span className="bulk-pkg__item-name">
                          {selectedMenuSummary.kind === 'custom' && it.category
                            ? `${it.name} (${it.category})`
                            : it.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {selectedMenuSummary.addons.length > 0 ? (
                <div className="bulk-menu-modal__section">
                  <p className="bulk-pkg__pricing-title">
                    <FaPlusCircle /> Selected add-ons
                  </p>
                  <ul className="bulk-pkg__items bulk-pkg__items--modal">
                    {selectedMenuSummary.addons.map((addon) => (
                      <li key={addon}>
                        <FaCheckCircle className="check-icon" />
                        <span className="bulk-pkg__item-name">{addon}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="bulk-menu-modal__actions">
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setSelectedMenuDetailsOpen(false);
                  setStep(1);
                  setErrorMsg('');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Change menu
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setSelectedMenuDetailsOpen(false)}
              >
                Continue with this menu
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Item / Package Preview Modal ── */}
      {previewItem && (
        <div
          className="item-modal-overlay"
          onClick={() => setPreviewItem(null)}
          role="dialog"
          aria-modal="true"
          aria-label={previewItem.name}
        >
          <div className="item-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="item-modal__close"
              onClick={() => setPreviewItem(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="item-modal__img">
              <img src={previewItem.image} alt={previewItem.name} />
            </div>
            <div className="item-modal__body">
              <h3 className="item-modal__name">{previewItem.name}</h3>
              {previewItem.tags && previewItem.tags.length > 0 && (
                <div className="item-modal__tags">
                  {previewItem.tags.map((t) => (
                    <span key={t} className="item-modal__tag">
                      <FaTag /> {t}
                    </span>
                  ))}
                </div>
              )}
              <p className="item-modal__desc">{previewItem.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Why choose us strip ── */}
      {step !== 3 && (
        <section className="bulk-why">
          <div className="container bulk-why__grid">
            {[
              {
                icon: <FaLeaf />,
                title: 'Fresh & Hygienic',
                text: 'Prepared in our certified kitchen with fresh ingredients daily.',
              },
              {
                icon: <FaUsers />,
                title: 'Experienced Catering',
                text: '500+ events served across Hyderabad with 5-star feedback.',
              },
              {
                icon: <FaClock />,
                title: 'On-Time Delivery',
                text: 'We take punctuality seriously. Never late, always hot.',
              },
              {
                icon: <FaUtensils />,
                title: 'Flexible Menus',
                text: 'Standard packages or full custom — we adapt to your vision.',
              },
              {
                icon: <FaCalendarAlt />,
                title: 'Easy Booking',
                text: '48h advance booking, dedicated event coordinator assigned.',
              },
              {
                icon: <FaSearch />,
                title: 'Transparent Pricing',
                text: 'No hidden charges. Detailed quote before you commit.',
              },
            ].map((w) => (
              <div key={w.title} className="bulk-why__card">
                <div className="bulk-why__icon">{w.icon}</div>
                <h4>{w.title}</h4>
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
